import { faArrowLeft, faArrowRight, faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { classNames } from "@/utils/classNames";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { PAGE_SIZE_OPTIONS, rememberPageSize } from "@/utils/preferredPageSize";

import { Icon } from "../Icon/Icon";

interface TablePaginationProps {
	currentPage: number;
	handleNavigateToPage: (page: number) => void;
	maxPage: number;
	limit: number;
	handleChangeRowsPerPage?: (rowsPerPage: number) => void;
	className?: string;
}

export const TablePagination: FunctionComponent<TablePaginationProps> = ({
	currentPage,
	handleNavigateToPage,
	handleChangeRowsPerPage,
	limit,
	maxPage,
	className,
}) => {
	const perPageRef = useRef<HTMLDivElement | null>(null);
	const perPageMenuRef = useRef<HTMLDivElement | null>(null);
	const [showPerPage, setShowPerPage] = useState(false);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(perPageMenuRef, showPerPage);
	const [perPagePosition, setPerPagePosition] = useState<{ top: number; left: number }>({
		top: 0,
		left: 0,
	});

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);
			const insideTrigger = perPageRef.current?.contains(target);
			const insideMenu = perPageMenuRef.current?.contains(target);

			if (!insideTrigger && !insideMenu) {
				setShowPerPage(false);
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, []);

	const handleTogglePerPage = () => {
		setShowPerPage((prev) => {
			const next = !prev;

			if (next && perPageRef.current) {
				const rect = perPageRef.current.getBoundingClientRect();
				const menuHeight = PAGE_SIZE_OPTIONS.length * 28 + 8;

				setPerPagePosition({
					top: rect.top - menuHeight - 4,
					left: rect.right,
				});
			}

			return next;
		});
	};

	const buildPages = (): { page: number; text: number | string }[] => {
		const allPages: { page: number; text: number | string }[] = [];

		for (let i = 1; i <= maxPage; i++) {
			allPages.push({ page: i, text: i });
		}

		if (maxPage > 7) {
			if (currentPage <= 4) {
				allPages.splice(6, maxPage - 7, { page: 7, text: "..." });
			} else if (currentPage >= maxPage - 3) {
				allPages.splice(1, maxPage - 7, { page: maxPage - 6, text: "..." });
			} else {
				allPages.splice(1, maxPage - 2);
				allPages.splice(
					1,
					0,
					{ page: currentPage - 2, text: "..." },
					{ page: currentPage - 1, text: currentPage - 1 },
					{ page: currentPage, text: currentPage },
					{ page: currentPage + 1, text: currentPage + 1 },
					{ page: currentPage + 2, text: "..." }
				);
			}
		}

		return allPages;
	};

	const handleGoToPage = (page: number) => {
		if (page === 0 || page > maxPage) {
			return;
		}

		handleNavigateToPage(page);
	};

	return (
		<div className={classNames("inline-flex items-center gap-2", className)}>
			<div className="flex items-center gap-0.5">
				<button
					aria-label="Previous page"
					className={classNames(
						"flex items-center justify-center min-w-[26px] h-6 px-1 rounded-sm",
						currentPage === 1
							? "text-text-disabled cursor-default"
							: "text-text-meta hover:text-text hover:bg-inactive-item cursor-pointer"
					)}
					disabled={currentPage === 1}
					type="button"
					onClick={() => handleGoToPage(currentPage - 1)}
				>
					<Icon className="w-3.5 h-3.5" icon={faArrowLeft} />
				</button>
				{buildPages().map((pageItem, idx) => {
					const { page, text } = pageItem;
					const isEllipsis = text === "...";
					const isActive = !isEllipsis && currentPage === page;

					return (
						<button
							key={`pagination-page-${page}-${idx}`}
							className={classNames(
								"min-w-[26px] h-6 px-1 text-xs font-medium rounded-sm",
								isEllipsis
									? "cursor-default text-text-meta"
									: isActive
										? "bg-inactive-item text-blue-action font-semibold cursor-pointer"
										: "text-text-meta hover:text-text hover:bg-inactive-item cursor-pointer"
							)}
							disabled={isEllipsis}
							type="button"
							onClick={isEllipsis ? undefined : () => handleNavigateToPage(page)}
						>
							{text}
						</button>
					);
				})}
				<button
					aria-label="Next page"
					className={classNames(
						"flex items-center justify-center min-w-[26px] h-6 px-1 rounded-sm",
						currentPage >= maxPage
							? "text-text-disabled cursor-default"
							: "text-text-meta hover:text-text hover:bg-inactive-item cursor-pointer"
					)}
					disabled={currentPage >= maxPage}
					type="button"
					onClick={() => handleGoToPage(currentPage + 1)}
				>
					<Icon className="w-3.5 h-3.5" icon={faArrowRight} />
				</button>
			</div>
			{handleChangeRowsPerPage && (
				<div ref={perPageRef} className="relative">
					<button
						aria-label="Rows per page"
						className="flex items-center gap-1 h-6 px-1.5 text-[11px] font-medium text-text-meta border border-border-light rounded-sm hover:text-text bg-white"
						type="button"
						onClick={handleTogglePerPage}
					>
						{limit}
						<Icon className="w-2.5 h-2.5" icon={faChevronDown} />
					</button>
					{showPerPage &&
						ReactDOM.createPortal(
							<div
								ref={perPageMenuRef}
								className="fixed z-[20000] bg-white border border-border-light rounded-md shadow-md overflow-hidden"
								style={{
									top: perPagePosition.top,
									left: perPagePosition.left,
									transform: "translateX(-100%)",
								}}
							>
								{PAGE_SIZE_OPTIONS.map((option) => (
									<button
										key={`per-page-${option}`}
										className={classNames(
											"block w-full text-left px-3 py-1.5 text-xs whitespace-nowrap hover:bg-inactive-item",
											option === limit ? "text-blue-action font-semibold" : "text-text-meta"
										)}
										type="button"
										onClick={() => {
											// Every selector table starts from the user's last pick, re-picks
											// included: that is how a size from a link becomes the default (PREC-4403).
											rememberPageSize(option);

											if (option !== limit) {
												handleChangeRowsPerPage(option);
											}

											setShowPerPage(false);
										}}
									>
										{option} per page
									</button>
								))}
							</div>,
							document.body
						)}
				</div>
			)}
		</div>
	);
};
